import React, { useEffect, useState } from 'react';
import { Chart } from 'primereact/chart';
import { ChartData, ChartOptions } from 'chart.js';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';
import 'primereact/resources/themes/lara-light-indigo/theme.css'; 
import 'primereact/resources/primereact.min.css'; 
import 'primeicons/primeicons.css';

// Definir tipos para los datos y opciones de los gráficos
interface ChartDataState {
    barData?: ChartData;
    pieData?: ChartData;
    lineData?: ChartData;
    polarData?: ChartData;
    radarData?: ChartData;
}

interface ChartOptionsState {
    barOptions?: ChartOptions;
    pieOptions?: ChartOptions;
    lineOptions?: ChartOptions;
    polarOptions?: ChartOptions;
    radarOptions?: ChartOptions;
}

interface MenuItem {
    label: string;
    icon?: string;
    to?: string;
    url?: string;
    target?: string;
    badge?: string;
    items?: MenuItem[];
    seperator?: boolean;
    preventExact?: boolean;
    class?: string;
}

const ChartDemo = () => {
    const [options, setOptions] = useState<ChartOptionsState>({});
    const [data, setChartData] = useState<ChartDataState>({});
    const [activeMenu, setActiveMenu] = useState<string>('');
    const navigate = useNavigate();

    // Modelo de menú adaptado para React Router
    const model: MenuItem[] = [
        {
            label: 'Home',
            items: [{ label: 'Dashboard', icon: 'pi pi-fw pi-home', to: '/inicio' }]
        },
        {
            label: 'Catálogos',
            items: [
                { label: 'Sucursales', icon: 'pi pi-fw pi-building', to: '/sucursales' },
                { label: 'Departamentos', icon: 'pi pi-fw pi-sitemap', to: '/departamentos' },
                { label: 'Proveedores', icon: 'pi pi-fw pi-address-book', to: '/proveedores' },
                { label: 'Usuarios', icon: 'pi pi-fw pi-users', to: '/usuarios' },
                { label: 'Líneas', icon: 'pi pi-fw pi-link', to: '/lineas' },
                { label: 'Sublíneas', icon: 'pi pi-fw pi-bars', to: '/sublineas' },
                { label: 'Marcas', icon: 'pi pi-fw pi-plus-square', to: '/marcas' },
                { label: 'Unidades', icon: 'pi pi-fw pi-tags', to: '/unidades' },
                { label: 'Productos', icon: 'pi pi-fw pi-cube', to: '/producto' }
            ]
        },
        {
            label: 'Movimientos',
            items: [
                { label: 'Entradas', icon: 'pi pi-fw pi-arrow-up', to: '/entrada' },
                { label: 'Salidas', icon: 'pi pi-fw pi-arrow-down', to: '/salida' },
                { label: 'Mantenimiento', icon: 'pi pi-fw pi-wrench', to: '/mantenimiento' },
                { label: 'Traspasos', icon: 'pi pi-fw pi-random', to: '/traspasos' }
            ]
        },
        {
            label: 'Consultas',
            items: [
                { label: 'Inventario', icon: 'pi pi-fw pi-warehouse', to: '/inventario' },
                { label: 'Saldos', icon: 'pi pi-fw pi-history', to: '/saldos' },
                { label: 'Consultor', icon: 'pi pi-fw pi-user-tie', to: '/consultor' }
            ]
        },
        {
            label: 'UI Components',
            items: [
                { label: 'Form Layout', icon: 'pi pi-fw pi-id-card', to: '/uikit/formlayout' },
                { label: 'Input', icon: 'pi pi-fw pi-check-square', to: '/uikit/input' },
                { label: 'Float Label', icon: 'pi pi-fw pi-bookmark', to: '/uikit/floatlabel' },
                { label: 'Invalid State', icon: 'pi pi-fw pi-exclamation-circle', to: '/uikit/invalidstate' },
                { label: 'Button', icon: 'pi pi-fw pi-mobile', to: '/uikit/button', class: 'rotated-icon' },
                { label: 'Table', icon: 'pi pi-fw pi-table', to: '/uikit/table' },
                { label: 'List', icon: 'pi pi-fw pi-list', to: '/uikit/list' },
                { label: 'Tree', icon: 'pi pi-fw pi-share-alt', to: '/uikit/tree' },
                { label: 'Panel', icon: 'pi pi-fw pi-tablet', to: '/uikit/panel' },
                { label: 'Overlay', icon: 'pi pi-fw pi-clone', to: '/uikit/overlay' },
                { label: 'Media', icon: 'pi pi-fw pi-image', to: '/uikit/media' },
                { label: 'Menu', icon: 'pi pi-fw pi-bars', to: '/uikit/menu', preventExact: true },
                { label: 'Message', icon: 'pi pi-fw pi-comment', to: '/uikit/message' },
                { label: 'File', icon: 'pi pi-fw pi-file', to: '/uikit/file' },
                { label: 'Chart', icon: 'pi pi-fw pi-chart-bar', to: '/uikit/charts' },
                { label: 'Misc', icon: 'pi pi-fw pi-circle', to: '/uikit/misc' }
            ]
        },
        {
            label: 'Prime Blocks',
            items: [
                { label: 'Free Blocks', icon: 'pi pi-fw pi-eye', to: '/blocks', badge: 'NEW' },
                { label: 'All Blocks', icon: 'pi pi-fw pi-globe', url: 'https://blocks.primereact.org', target: '_blank' }
            ]
        },
        {
            label: 'Utilities',
            items: [
                { label: 'PrimeIcons', icon: 'pi pi-fw pi-prime', to: '/utilities/icons' },
                { label: 'PrimeFlex', icon: 'pi pi-fw pi-desktop', url: 'https://primeflex.org/', target: '_blank' }
            ]
        },
        {
            label: 'Pages',
            icon: 'pi pi-fw pi-briefcase',
            to: '/pages',
            items: [
                {
                    label: 'Landing',
                    icon: 'pi pi-fw pi-globe',
                    to: '/landing'
                },
                {
                    label: 'Auth',
                    icon: 'pi pi-fw pi-user',
                    items: [
                        {
                            label: 'Login',
                            icon: 'pi pi-fw pi-sign-in',
                            to: '/login'
                        },
                        {
                            label: 'Error',
                            icon: 'pi pi-fw pi-times-circle',
                            to: '/auth/error'
                        },
                        {
                            label: 'Access Denied',
                            icon: 'pi pi-fw pi-lock',
                            to: '/auth/access'
                        }
                    ]
                },
                {
                    label: 'Crud',
                    icon: 'pi pi-fw pi-pencil',
                    to: '/pages/crud'
                },
                {
                    label: 'Timeline',
                    icon: 'pi pi-fw pi-calendar',
                    to: '/pages/timeline'
                },
                {
                    label: 'Not Found',
                    icon: 'pi pi-fw pi-exclamation-circle',
                    to: '/pages/notfound'
                },
                {
                    label: 'Empty',
                    icon: 'pi pi-fw pi-circle-off',
                    to: '/pages/empty'
                }
            ]
        },
        {
            label: 'Hierarchy',
            items: [
                {
                    label: 'Submenu 1',
                    icon: 'pi pi-fw pi-bookmark',
                    items: [
                        {
                            label: 'Submenu 1.1',
                            icon: 'pi pi-fw pi-bookmark',
                            items: [
                                { label: 'Submenu 1.1.1', icon: 'pi pi-fw pi-bookmark' },
                                { label: 'Submenu 1.1.2', icon: 'pi pi-fw pi-bookmark' },
                                { label: 'Submenu 1.1.3', icon: 'pi pi-fw pi-bookmark' }
                            ]
                        },
                        {
                            label: 'Submenu 1.2',
                            icon: 'pi pi-fw pi-bookmark',
                            items: [{ label: 'Submenu 1.2.1', icon: 'pi pi-fw pi-bookmark' }]
                        }
                    ]
                },
                {
                    label: 'Submenu 2',
                    icon: 'pi pi-fw pi-bookmark',
                    items: [
                        {
                            label: 'Submenu 2.1',
                            icon: 'pi pi-fw pi-bookmark',
                            items: [
                                { label: 'Submenu 2.1.1', icon: 'pi pi-fw pi-bookmark' },
                                { label: 'Submenu 2.1.2', icon: 'pi pi-fw pi-bookmark' }
                            ]
                        },
                        {
                            label: 'Submenu 2.2',
                            icon: 'pi pi-fw pi-bookmark',
                            items: [{ label: 'Submenu 2.2.1', icon: 'pi pi-fw pi-bookmark' }]
                        }
                    ]
                }
            ]
        },
        {
            label: 'Get Started',
            items: [
                {
                    label: 'Documentation',
                    icon: 'pi pi-fw pi-question',
                    to: '/documentation'
                },
                {
                    label: 'Figma',
                    url: 'https://www.dropbox.com/scl/fi/bhfwymnk8wu0g5530ceas/sakai-2023.fig?rlkey=u0c8n6xgn44db9t4zkd1brr3l&dl=0',
                    icon: 'pi pi-fw pi-pencil',
                    target: '_blank'
                },
                {
                    label: 'View Source',
                    icon: 'pi pi-fw pi-search',
                    url: 'https://github.com/primefaces/sakai-react',
                    target: '_blank'
                }
            ]
        }
    ];

    const handleMenuClick = (item: MenuItem) => {
        if (item.to) {
            navigate(item.to);
        } else if (item.url) {
            if (item.target === '_blank') {
                window.open(item.url, '_blank');
            } else {
                window.location.href = item.url;
            }
        }
    };

    const toggleSubmenu = (label: string) => {
        setActiveMenu(activeMenu === label ? '' : label);
    };

    const renderMenuItem = (item: MenuItem, level: number = 0): JSX.Element => {
        const hasSubmenu = item.items && item.items.length > 0;
        const isActive = activeMenu === item.label;
        const paddingLeft = level * 20 + 15;

        return (
            <li key={item.label} className={`nav-item ${hasSubmenu ? 'has-treeview' : ''} ${isActive ? 'menu-open' : ''}`}>
                <a
                    href="#"
                    className={`nav-link ${isActive ? 'active' : ''}`}
                    onClick={(e) => {
                        e.preventDefault();
                        if (hasSubmenu) {
                            toggleSubmenu(item.label);
                        } else {
                            handleMenuClick(item);
                        }
                    }}
                    style={{ paddingLeft: `${paddingLeft}px` }}
                >
                    {item.icon && <i className={`${item.icon} nav-icon`}></i>}
                    <p>
                        {item.label}
                        {item.badge && <span className="badge badge-info right">{item.badge}</span>}
                        {hasSubmenu && <i className="fas fa-angle-left right"></i>}
                    </p>
                </a>
                {hasSubmenu && (
                    <ul className={`nav nav-treeview ${isActive ? 'd-block' : 'd-none'}`}>
                        {item.items!.map(subItem => renderMenuItem(subItem, level + 1))}
                    </ul>
                )}
            </li>
        );
    };

    useEffect(() => {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color') || '#495057';
        const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary') || '#6c757d';
        const surfaceBorder = documentStyle.getPropertyValue('--surface-border') || '#dfe7ef';

        const barData: ChartData = {
            labels: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio'],
            datasets: [
                {
                    label: 'Ventas 2024',
                    backgroundColor: documentStyle.getPropertyValue('--primary-500') || '#6366f1',
                    borderColor: documentStyle.getPropertyValue('--primary-500') || '#6366f1',
                    data: [65, 59, 80, 81, 56, 55, 40]
                },
                {
                    label: 'Ventas 2023',
                    backgroundColor: documentStyle.getPropertyValue('--primary-200') || '#bcbdf9',
                    borderColor: documentStyle.getPropertyValue('--primary-200') || '#bcbdf9',
                    data: [28, 48, 40, 19, 86, 27, 90]
                }
            ]
        };

        const barOptions: ChartOptions = {
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: textColorSecondary,
                        font: {
                          
                        }
                    },
                    grid: {
                        display: false
                    },
                    border: {
                        display: false
                    }
                },
                y: {
                    ticks: {
                        color: textColorSecondary
                    },
                    grid: {
                        color: surfaceBorder
                    },
                    border: {
                        display: false
                    }
                }
            }
        };

        const pieData: ChartData = {
            labels: ['Productos A', 'Productos B', 'Productos C'],
            datasets: [
                {
                    data: [540, 325, 702],
                    backgroundColor: [
                        documentStyle.getPropertyValue('--indigo-500') || '#6366f1', 
                        documentStyle.getPropertyValue('--purple-500') || '#a855f7', 
                        documentStyle.getPropertyValue('--teal-500') || '#14b8a6'
                    ],
                    hoverBackgroundColor: [
                        documentStyle.getPropertyValue('--indigo-400') || '#8183f4', 
                        documentStyle.getPropertyValue('--purple-400') || '#b975f9', 
                        documentStyle.getPropertyValue('--teal-400') || '#41c5b7'
                    ]
                }
            ]
        };

        const pieOptions: ChartOptions = {
            plugins: {
                legend: {
                    labels: {
                        usePointStyle: true,
                        color: textColor
                    }
                }
            }
        };

        const lineData: ChartData = {
            labels: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio'],
            datasets: [
                {
                    label: 'Inventario Entrada',
                    data: [65, 59, 80, 81, 56, 55, 40],
                    fill: false,
                    backgroundColor: documentStyle.getPropertyValue('--primary-500') || '#6366f1',
                    borderColor: documentStyle.getPropertyValue('--primary-500') || '#6366f1',
                    tension: 0.4
                },
                {
                    label: 'Inventario Salida',
                    data: [28, 48, 40, 19, 86, 27, 90],
                    fill: false,
                    backgroundColor: documentStyle.getPropertyValue('--primary-200') || '#bcbdf9',
                    borderColor: documentStyle.getPropertyValue('--primary-200') || '#bcbdf9',
                    tension: 0.4
                }
            ]
        };

        const lineOptions: ChartOptions = {
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: textColorSecondary
                    },
                    grid: {
                        color: surfaceBorder
                    },
                    border: {
                        display: false
                    }
                },
                y: {
                    ticks: {
                        color: textColorSecondary
                    },
                    grid: {
                        color: surfaceBorder
                    },
                    border: {
                        display: false
                    }
                }
            }
        };

        const polarData: ChartData = {
            datasets: [
                {
                    data: [11, 16, 7, 3],
                    backgroundColor: [
                        documentStyle.getPropertyValue('--indigo-500') || '#6366f1',
                        documentStyle.getPropertyValue('--purple-500') || '#a855f7',
                        documentStyle.getPropertyValue('--teal-500') || '#14b8a6',
                        documentStyle.getPropertyValue('--orange-500') || '#f97316'
                    ],
                    label: 'Categorías de Productos'
                }
            ],
            labels: ['Electrónicos', 'Ropa', 'Hogar', 'Deportes']
        };

        const polarOptions: ChartOptions = {
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                r: {
                    grid: {
                        color: surfaceBorder
                    }
                }
            }
        };

        const radarData: ChartData = {
            labels: ['Ventas', 'Marketing', 'Desarrollo', 'Soporte', 'Tecnología', 'Administración'],
            datasets: [
                {
                    label: 'Rendimiento Q1',
                    borderColor: documentStyle.getPropertyValue('--indigo-400') || '#8183f4',
                    pointBackgroundColor: documentStyle.getPropertyValue('--indigo-400') || '#8183f4',
                    pointBorderColor: documentStyle.getPropertyValue('--indigo-400') || '#8183f4',
                    pointHoverBackgroundColor: textColor,
                    pointHoverBorderColor: documentStyle.getPropertyValue('--indigo-400') || '#8183f4',
                    data: [65, 59, 90, 81, 56, 55]
                },
                {
                    label: 'Rendimiento Q2',
                    borderColor: documentStyle.getPropertyValue('--purple-400') || '#b975f9',
                    pointBackgroundColor: documentStyle.getPropertyValue('--purple-400') || '#b975f9',
                    pointBorderColor: documentStyle.getPropertyValue('--purple-400') || '#b975f9',
                    pointHoverBackgroundColor: textColor,
                    pointHoverBorderColor: documentStyle.getPropertyValue('--purple-400') || '#b975f9',
                    data: [28, 48, 40, 19, 96, 27]
                }
            ]
        };

        const radarOptions: ChartOptions = {
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                r: {
                    grid: {
                        color: textColorSecondary
                    }
                }
            }
        };

        setOptions({
            barOptions,
            pieOptions,
            lineOptions,
            polarOptions,
            radarOptions
        });

        setChartData({
            barData,
            pieData,
            lineData,
            polarData,
            radarData
        });
    }, []);

    return (
        <div className="wrapper">
            <Navbar />
            <Sidebar />
            
            {/* Content Wrapper */}
            <div className="content-wrapper" style={{ marginLeft: '250px', padding: '20px' }}>
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1 className="m-0">Dashboard de Gráficos</h1>
                            </div>
                            <div className="col-sm-6">
                                <ol className="breadcrumb float-sm-right">
                                    <li className="breadcrumb-item">
                                        <a href="/inicio">Inicio</a>
                                    </li>
                                    <li className="breadcrumb-item active">Gráficos</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Menu Navigation Section */}
                <section className="content mb-4">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            <i className="fas fa-bars mr-1"></i>
                                            Menú de Navegación
                                        </h3>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <nav className="mt-2">
                                                    <ul className="nav nav-pills nav-sidebar flex-column" data-widget="treeview" role="menu" data-accordion="false">
                                                        {model.slice(0, Math.ceil(model.length / 2)).map((item, i) => (
                                                            !item?.seperator ? renderMenuItem(item) : <li key={i} className="menu-separator"></li>
                                                        ))}
                                                    </ul>
                                                </nav>
                                            </div>
                                            <div className="col-md-6">
                                                <nav className="mt-2">
                                                    <ul className="nav nav-pills nav-sidebar flex-column" data-widget="treeview" role="menu" data-accordion="false">
                                                        {model.slice(Math.ceil(model.length / 2)).map((item, i) => (
                                                            !item?.seperator ? renderMenuItem(item) : <li key={i} className="menu-separator"></li>
                                                        ))}
                                                    </ul>
                                                </nav>
                                            </div>
                                        </div>
                                        <div className="row mt-3">
                                            <div className="col-12 text-center">
                                                <a href="https://blocks.primereact.org" target="_blank" rel="noopener noreferrer" style={{ cursor: 'pointer' }}>
                                                    <img alt="Prime Blocks" className="img-fluid" src="/dist/img/banner-primeblocks.png" style={{ maxWidth: '300px' }} />
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Main content */}
                <section className="content">
                    <div className="container-fluid">
                        <div className="row">
                            {/* Gráfico Lineal */}
                            <div className="col-12 col-xl-6">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            <i className="fas fa-chart-line mr-1"></i>
                                            Gráfico Lineal - Movimientos de Inventario
                                        </h3>
                                    </div>
                                    <div className="card-body">
                                        <Chart type="line" data={data.lineData} options={options.lineOptions} />
                                    </div>
                                </div>
                            </div>

                            {/* Gráfico de Barras */}
                            <div className="col-12 col-xl-6">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            <i className="fas fa-chart-bar mr-1"></i>
                                            Gráfico de Barras - Comparativo de Ventas
                                        </h3>
                                    </div>
                                    <div className="card-body">
                                        <Chart type="bar" data={data.barData} options={options.barOptions} />
                                    </div>
                                </div>
                            </div>

                            {/* Gráfico Circular */}
                            <div className="col-12 col-xl-6">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            <i className="fas fa-chart-pie mr-1"></i>
                                            Gráfico Circular - Distribución de Productos
                                        </h3>
                                    </div>
                                    <div className="card-body d-flex justify-content-center">
                                        <Chart type="pie" data={data.pieData} options={options.pieOptions} />
                                    </div>
                                </div>
                            </div>

                            {/* Gráfico Dona */}
                            <div className="col-12 col-xl-6">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            <i className="fas fa-chart-pie mr-1"></i>
                                            Gráfico Dona - Categorías
                                        </h3>
                                    </div>
                                    <div className="card-body d-flex justify-content-center">
                                        <Chart type="doughnut" data={data.pieData} options={options.pieOptions} />
                                    </div>
                                </div>
                            </div>

                            {/* Gráfico Polar */}
                            <div className="col-12 col-xl-6">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            <i className="fas fa-chart-area mr-1"></i>
                                            Gráfico Polar - Análisis por Categoría
                                        </h3>
                                    </div>
                                    <div className="card-body d-flex justify-content-center">
                                        <Chart type="polarArea" data={data.polarData} options={options.polarOptions} />
                                    </div>
                                </div>
                            </div>

                            {/* Gráfico Radar */}
                            <div className="col-12 col-xl-6">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            <i className="fas fa-chart-area mr-1"></i>
                                            Gráfico Radar - Rendimiento por Departamento
                                        </h3>
                                    </div>
                                    <div className="card-body d-flex justify-content-center">
                                        <Chart type="radar" data={data.radarData} options={options.radarOptions} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default ChartDemo;